import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './CompanyManagement.css';
import { getAuthHeaders } from '../utils/authHeaders';
const API = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

function CompanyManagement() {
  const [companies, setCompanies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  const [activeTab, setActiveTab] = useState('All Companies');
  const [sortOrder, setSortOrder] = useState('None');

  const [activeDropdownId, setActiveDropdownId] = useState(null);
  const [companyToArchive, setCompanyToArchive] = useState(null);
  const [companyToUnarchive, setCompanyToUnarchive] = useState(null);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [companyToActivate, setCompanyToActivate] = useState(null);
  const [summaryModal, setSummaryModal] = useState(null);

  const dropdownRefs = useRef({});
 const navigate = useNavigate();

const openDashboard = (companyId) => {
    localStorage.setItem('selectedCompanyId', companyId.toString());
    navigate(`/dashboard/${companyId}/overview`);
};
  const fetchCompaniesData = () => {
    setLoading(true);
    fetch(API + '/companies', { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          navigate('/login');
          throw new Error('Not authorized - please log in with a Holding-level account.');
        }
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then(baseData => {
        setCompanies(baseData.companies || []);
        setActivities(baseData.activities || []);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };

  useEffect(() => { fetchCompaniesData(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdownId === null) return;
      const currentEl = dropdownRefs.current[activeDropdownId];
      if (currentEl && !currentEl.contains(event.target)) {
        setActiveDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeDropdownId]);

  const handleConfirmArchive = () => {
    if (!companyToArchive) return;
    setCompanies(prev => prev.map(c => c.id === companyToArchive.id ? { ...c, status: 'Archived' } : c));
    setCompanyToArchive(null);
    setActiveDropdownId(null);
    fetch(`${API}/companies/${companyToArchive.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status: 'Archived' })
    }).then(() => fetchCompaniesData()).catch(err => console.error("Backend sync error:", err));
  };

  const handleConfirmUnarchive = () => {
    if (!companyToUnarchive) return;
    setCompanies(prev => prev.map(c => c.id === companyToUnarchive.id ? { ...c, status: 'Pending Setup' } : c));
    setCompanyToUnarchive(null);
    fetch(`${API}/companies/${companyToUnarchive.id}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ status: 'Pending Setup' })
    }).then(() => fetchCompaniesData()).catch(err => console.error("Backend sync error:", err));
  };

  const handleConfirmDelete = () => {
    if (!companyToDelete) return;
    const idToDelete = companyToDelete.id;
    setActiveDropdownId(null);
    fetch(`${API}/companies/${idToDelete}`, {
      method: 'DELETE', headers: getAuthHeaders({ 'Accept': 'application/json' })
    }).then(res => {
      if (!res.ok) throw new Error('Failed to delete company.');
      return res.json();
    }).then(() => {
      setCompanies(prev => prev.filter(c => c.id !== idToDelete));
      setCompanyToDelete(null);
    }).catch(err => {
      console.error("Delete error:", err);
      setCompanyToDelete(null);
      alert('Failed to delete company. Check the backend console for details.');
    });
  };

  const handleConfirmActivate = () => {
    if (!companyToActivate) return;
    const id = companyToActivate.id;
    fetch(`${API}/companies/${id}/activate`, {
      method: 'POST',headers: getAuthHeaders()
    }).then(async (res) => {
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Activation failed.');
      setCompanyToActivate(null);
      openDashboard(id);
    }).catch(err => alert(err.message));
  };

  // ── Styles ──
  const tabBtnStyle = (isActive) => ({
    padding: '8px 16px', borderRadius: '999px', border: isActive ? 'none' : '1px solid #cbd5e1',
    backgroundColor: isActive ? '#2563eb' : '#fff', color: isActive ? '#fff' : '#64748b',
    fontSize: '0.85rem', fontWeight: isActive ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s'
  });

  const summaryCardStyle = {
    backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px',
    padding: '16px 20px', cursor: 'pointer', transition: 'box-shadow 0.2s'
  };

  if (initialLoading) return <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading Companies...</div>;
  if (error) return <div className="error" style={{ color: 'red', padding: '20px' }}>Error: {error}</div>;

  const totalCount = companies.length;
  const activeCount = companies.filter(c => c.status === 'Active').length;
  const pendingCount = companies.filter(c => c.status === 'Pending Setup' || c.status === 'Inactive').length;
  const archivedCount = companies.filter(c => c.status === 'Archived').length;

  const getSummaryModalCompanies = () => {
    if (!summaryModal) return [];
    switch (summaryModal.type) {
      case 'active': return companies.filter(c => c.status === 'Active');
      case 'pending': return companies.filter(c => c.status === 'Pending Setup' || c.status === 'Inactive');
      case 'archived': return companies.filter(c => c.status === 'Archived');
      default: return companies;
    }
  };

  const openCompanyFromModal = (company) => {
    setSummaryModal(null);
    if (company.status === 'Pending Setup' || company.status === 'Inactive') {
      setCompanyToActivate(company);
    } else {
     openDashboard(company.id);
    }
  };

  let processedCompanies = companies;
  if (activeTab === 'Active Companies') processedCompanies = processedCompanies.filter(c => c.status === 'Active');
  else if (activeTab === 'Pending Setup') processedCompanies = processedCompanies.filter(c => c.status === 'Pending Setup' || c.status === 'Inactive');
  else if (activeTab === 'Archived Companies') processedCompanies = processedCompanies.filter(c => c.status === 'Archived');
  else processedCompanies = processedCompanies.filter(c => c.status !== 'Archived');

  if (sortOrder === 'AZ') processedCompanies.sort((a, b) => a.name.localeCompare(b.name));
  else if (sortOrder === 'ZA') processedCompanies.sort((a, b) => b.name.localeCompare(a.name));

  // Status badge colors
  const statusColors = {
    'Active': { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
    'Pending Setup': { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    'Inactive': { bg: '#fef3c7', color: '#92400e', border: '#fde68a' },
    'Archived': { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' },
  };

  const getStatusStyle = (status) => {
    const c = statusColors[status] || statusColors['Pending Setup'];
    return { display: 'inline-block', padding: '3px 10px', borderRadius: '999px', fontSize: '0.7rem', fontWeight: '700', backgroundColor: c.bg, color: c.color, border: `1px solid ${c.border}`, textTransform: 'uppercase', letterSpacing: '0.03em' };
  };

  // Action button styles
  const actionBtn = { padding: '7px 14px', fontSize: '0.8rem', backgroundColor: '#0f172a', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fff', fontWeight: '500', whiteSpace: 'nowrap' };
  const primaryActionBtn = { ...actionBtn, backgroundColor: '#2563eb' };
  const setupBtn = { ...actionBtn, backgroundColor: '#16a34a' };

  // Metric card style
  const metricStyle = { display: 'flex', flexDirection: 'column', gap: '2px' };
  const metricLabelStyle = { fontSize: '0.7rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' };
  const metricValueStyle = { fontSize: '1rem', fontWeight: '700', color: '#0f172a' };

  return (
    <div className="management-container">

      <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0f172a', margin: '0 0 20px 0' }}>Company Management</h1>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={summaryCardStyle} onClick={() => setSummaryModal({ type: 'total', title: 'All Companies' })}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Total Companies</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#0f172a' }}>{totalCount}</p>
        </div>
        <div style={summaryCardStyle} onClick={() => setSummaryModal({ type: 'active', title: 'Active Companies' })}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Active Companies</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#10b981' }}>{activeCount}</p>
        </div>
        <div style={summaryCardStyle} onClick={() => setSummaryModal({ type: 'pending', title: 'Companies Pending Setup' })}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Pending Setup</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#eab308' }}>{pendingCount}</p>
        </div>
        <div style={summaryCardStyle} onClick={() => setSummaryModal({ type: 'archived', title: 'Archived Companies' })}>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '0.85rem', color: '#64748b', fontWeight: '600' }}>Archived Companies</h3>
          <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: '700', color: '#0f172a' }}>{archivedCount}</p>
        </div>
      </div>

      {/* Tabs + Create */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button style={tabBtnStyle(activeTab === 'All Companies')} onClick={() => setActiveTab('All Companies')}>All Companies</button>
          <button style={tabBtnStyle(activeTab === 'Active Companies')} onClick={() => setActiveTab('Active Companies')}>Active Companies</button>
          <button style={tabBtnStyle(activeTab === 'Pending Setup')} onClick={() => setActiveTab('Pending Setup')}>Pending Setup</button>
          <button style={tabBtnStyle(activeTab === 'Archived Companies')} onClick={() => setActiveTab('Archived Companies')}>Archived Companies</button>
        </div>
        <button className="btn-primary" onClick={() => navigate('/companies/create')}>Create New Company</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' }}>

        {/* Left: Company List */}
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f172a' }}>Company Performance</h2>
            <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#0f172a', fontSize: '0.85rem', fontWeight: '500', outline: 'none', cursor: 'pointer' }}>
              <option value="None">Default Order</option>
              <option value="AZ">Alphabetical (A - Z)</option>
              <option value="ZA">Alphabetical (Z - A)</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {processedCompanies.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.9rem', fontStyle: 'italic', textAlign: 'center', padding: '40px' }}>No companies found matching this filter.</p>
            ) : processedCompanies.map((company) => {
              const isPending = company.status === 'Pending Setup' || company.status === 'Inactive';
              const isArchived = company.status === 'Archived';
              const isMenuOpen = activeDropdownId === company.id;
              const modules = company.assigned_modules || [];
              const adminName = company.administrators?.length ? company.administrators.map(user => user.name).join(', ') : (company.admin ? company.admin.name : 'Unassigned');

              return (
                <div key={company.id} style={{
                  backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', transition: 'box-shadow 0.2s',
                }}>

                  {/* ── Row 1: Header ── */}
                  <div style={{ padding: '18px 20px 14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    {/* Left: Logo + Name + Status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{
                        width: '44px', height: '44px', borderRadius: '10px', backgroundColor: '#f1f5f9',
                        border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', color: '#94a3b8', fontWeight: '600', flexShrink: 0,
                      }}>Logo</div>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '2px' }}>
                          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '700', color: '#0f172a' }}>
                            <button
                              type="button"
                              className="company-dashboard-title"
                              onClick={() => openDashboard(company.id)}
                              aria-label={`Open ${company.name} dashboard`}
                            >
                              {company.name}
                            </button>
                          </h3>
                          <span style={getStatusStyle(company.status)}>{company.status}</span>
                        </div>
                        {company.legal_name && (
                          <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>{company.legal_name}</p>
                        )}
                      </div>
                    </div>

                    {/* Right: Buttons */}
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                      <button onClick={() => navigate(`/companies/edit/${company.id}`)} style={actionBtn}>Edit</button>
                      <button onClick={() => navigate(`/companies/edit/${company.id}`)} style={actionBtn}>Modules</button>
                      <button onClick={() => navigate(`/companies/edit/${company.id}`)} style={actionBtn}>Users</button>

                      {isArchived ? (
                        <button style={primaryActionBtn} onClick={() => setCompanyToUnarchive(company)}>Unarchive</button>
                      ) : isPending ? (
                        <button style={setupBtn} onClick={() => setCompanyToActivate(company)}>Continue Setup</button>
                      ) : (
                      <button style={primaryActionBtn} onClick={() => openDashboard(company.id)}>Open Dashboard</button>                   
                      )}

                      {/* ⋮ Menu */}
                      <div style={{ position: 'relative' }} ref={(el) => { dropdownRefs.current[company.id] = el; }}>
                        <button type="button" onClick={(e) => { e.stopPropagation(); setActiveDropdownId(isMenuOpen ? null : company.id); }}
                          style={{
                            background: 'none', border: '1px solid #e2e8f0', borderRadius: '6px',
                            width: '34px', height: '34px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', color: '#64748b', fontSize: '1rem', fontWeight: 'bold', backgroundColor: '#fff'
                          }}>⋮</button>

                        {isMenuOpen && (
                          <div style={{
                            position: 'absolute', right: 0, top: '38px', backgroundColor: '#fff',
                            border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            zIndex: 999, minWidth: '140px', overflow: 'hidden'
                          }}>
                            {!isArchived && (
                              isPending ? (
                                <button type="button" disabled style={{ width: '100%', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', border: 'none', fontSize: '0.85rem', color: '#cbd5e1', fontWeight: '600', cursor: 'not-allowed' }}>Archive</button>
                              ) : (
                                <button type="button" onClick={(e) => { e.stopPropagation(); setActiveDropdownId(null); setCompanyToArchive(company); }}
                                  style={{ width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', fontSize: '0.85rem', color: '#ef4444', fontWeight: '600', cursor: 'pointer' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>Archive</button>
                              )
                            )}
                            <button type="button" onClick={(e) => { e.stopPropagation(); setActiveDropdownId(null); setCompanyToDelete(company); }}
                              style={{ width: '100%', padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none', borderTop: !isArchived ? '1px solid #f1f5f9' : 'none', fontSize: '0.85rem', color: '#dc2626', fontWeight: '600', cursor: 'pointer' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#fef2f2'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>Delete</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Row 2: Info Strip ── */}
                  <div style={{
                    padding: '10px 20px', backgroundColor: '#f8fafc', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9',
                    display: 'flex', gap: '24px', flexWrap: 'wrap', fontSize: '0.8rem', color: '#475569',
                  }}>
                    <span>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Modules: </span>
                      {modules.length > 0 ? modules.map(m => m.name).join(', ') : <span style={{ color: '#cbd5e1' }}>None</span>}
                    </span>
                    <span>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Administrators: </span>
                      <span style={{ color: adminName === 'Unassigned' ? '#dc2626' : '#475569' }}>{adminName}</span>{company.privacy_settings?.isolate_from_holding && <strong style={{ color: '#7c3aed', marginLeft: 12 }}>Private</strong>}
                    </span>
                    <span>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Created: </span>
                      {new Date(company.created_at).toLocaleDateString()}
                    </span>
                    <span>
                      <span style={{ color: '#94a3b8', fontWeight: '600' }}>Setup: </span>
                      <span style={{ color: company.setup_completion_percentage >= 100 ? '#16a34a' : '#d97706', fontWeight: '600' }}>
                        {company.setup_completion_percentage}%
                      </span>
                    </span>
                  </div>

                  {/* ── Row 3: Metrics Grid ── */}
                  <div style={{
                    padding: '14px 20px 16px 20px',
                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px',
                  }}>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Gross Sales</span>
                      <span style={metricValueStyle}>${(company.gross_sales || 0).toLocaleString()}</span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Expenses</span>
                      <span style={metricValueStyle}>${(company.expenses || 0).toLocaleString()}</span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Net Profit</span>
                      <span style={{ ...metricValueStyle, color: (company.net_profit || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                        ${(company.net_profit || 0).toLocaleString()}
                      </span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Profit Margin</span>
                      <span style={{ ...metricValueStyle, color: (company.profit_margin || 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                        {company.gross_sales > 0 ? `${parseFloat(company.profit_margin).toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Employees</span>
                      <span style={metricValueStyle}>{company.employee_count || 0}</span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Projects</span>
                      <span style={metricValueStyle}>{company.active_projects || 0}</span>
                    </div>
                    <div style={metricStyle}>
                      <span style={metricLabelStyle}>Receivables</span>
                      <span style={metricValueStyle}>${(company.outstanding_receivables || 0).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Activity Sidebar */}
        <div style={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', position: 'sticky', top: '20px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: '0 0 12px 0', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
            Recent Activity
          </h3>
          <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingRight: '4px' }}>
            {activities.length === 0 ? (
              <p style={{ fontSize: '0.85rem', color: '#64748b', fontStyle: 'italic', textAlign: 'center', margin: '20px 0' }}>No recent activity recorded.</p>
            ) : activities.map((act, index) => (
              <div key={index} onClick={() => act.company?.id && navigate(`/dashboard/${act.company.id}/overview`)}
                style={{ fontSize: '0.85rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '10px', cursor: act.company?.id ? 'pointer' : 'default' }}>
                <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#1e293b' }}>{act.type || 'Company Updated'}</p>
                <p style={{ margin: '0 0 4px 0', color: '#64748b' }}>{act.company?.name || 'Changes made to company record.'}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#94a3b8' }}>
                  <span>By: {act.user?.name || 'Admin'}</span>
                  <span>{act.created_at ? new Date(act.created_at).toLocaleDateString() : 'Just now'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Card Breakdown Popup */}
      {summaryModal && (
        <div className="modal-overlay" onClick={() => setSummaryModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{summaryModal.title}</h2>
              <button className="btn-close" onClick={() => setSummaryModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-company-list">
                {getSummaryModalCompanies().length > 0 ? (
                  getSummaryModalCompanies().map(company => (
                    <div key={company.id} className="modal-company-row" onClick={() => openCompanyFromModal(company)}>
                      <div className="modal-company-info">
                        <span className="modal-company-name">{company.name}</span>
                        <span className="modal-metric-detail">
                          {summaryModal.type === 'pending'
                            ? `Created: ${company.created_at ? new Date(company.created_at).toLocaleDateString() : 'N/A'} · Status: ${company.status}`
                            : `Status: ${company.status}`}
                        </span>
                      </div>
                      <span className="modal-company-link">
                        {summaryModal.type === 'pending' ? 'Continue Setup →' : 'Open →'}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="empty-state">No companies found in this category.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Modal */}
      {companyToArchive && (
        <div className="modal-overlay" onClick={() => setCompanyToArchive(null)} style={{ zIndex: 3000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '380px', textAlign: 'center' }}>
            <div className="modal-header" style={{ borderBottom: 'none', justifyContent: 'center' }}>
              <h2 style={{ fontSize: '1.2rem' }}>Are you sure?</h2>
            </div>
            <div className="modal-body" style={{ paddingBottom: '20px' }}>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 20px 0' }}>
                Are you sure you want to archive <strong>{companyToArchive.name}</strong>?
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button onClick={() => setCompanyToArchive(null)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Decline</button>
                <button onClick={handleConfirmArchive}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#ef4444', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unarchive Modal */}
      {companyToUnarchive && (
        <div className="modal-overlay" onClick={() => setCompanyToUnarchive(null)} style={{ zIndex: 3000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '380px', textAlign: 'center' }}>
            <div className="modal-header" style={{ borderBottom: 'none', justifyContent: 'center' }}>
              <h2 style={{ fontSize: '1.2rem' }}>Unarchive Company?</h2>
            </div>
            <div className="modal-body" style={{ paddingBottom: '20px' }}>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 20px 0' }}>
                Are you sure you want to unarchive <strong>{companyToUnarchive.name}</strong>? This will set its status to pending setup.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button onClick={() => setCompanyToUnarchive(null)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Decline</button>
                <button onClick={handleConfirmUnarchive}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {companyToDelete && (
        <div className="modal-overlay" onClick={() => setCompanyToDelete(null)} style={{ zIndex: 3000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '400px', textAlign: 'center' }}>
            <div className="modal-header" style={{ borderBottom: 'none', justifyContent: 'center' }}>
              <h2 style={{ fontSize: '1.2rem', color: '#dc2626' }}>Delete Company?</h2>
            </div>
            <div className="modal-body" style={{ paddingBottom: '20px' }}>
              <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 8px 0' }}>
                This will permanently delete <strong>{companyToDelete.name}</strong> and all of its contracts, employees, departments, and activity history.
              </p>
              <p style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: '600', margin: '0 0 20px 0' }}>This cannot be undone.</p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button onClick={() => setCompanyToDelete(null)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
                <button onClick={handleConfirmDelete}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#dc2626', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activate Modal */}
      {companyToActivate && (
        <div className="modal-overlay" onClick={() => setCompanyToActivate(null)} style={{ zIndex: 3000 }}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ width: '420px' }}>
            <div className="modal-header" style={{ borderBottom: 'none', justifyContent: 'center' }}>
              <h2 style={{ fontSize: '1.2rem' }}>Activate {companyToActivate.name}?</h2>
            </div>
            <div className="modal-body" style={{ paddingBottom: '20px' }}>
              <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '14px', marginBottom: '16px', fontSize: '0.85rem', color: '#334155', textAlign: 'left' }}>
                <div style={{ marginBottom: '6px' }}><strong>Name:</strong> {companyToActivate.name || <span style={{ color: '#dc2626', fontWeight: '600' }}>Missing</span>}</div>
                <div style={{ marginBottom: '6px' }}><strong>Phone:</strong> {companyToActivate.phone || <span style={{ color: '#dc2626', fontWeight: '600' }}>Missing</span>}</div>
                <div style={{ marginBottom: '6px' }}><strong>Location:</strong> {companyToActivate.location || <span style={{ color: '#dc2626', fontWeight: '600' }}>Missing</span>}</div>
                <div style={{ marginBottom: '6px' }}><strong>Admin:</strong> {companyToActivate.admin ? companyToActivate.admin.name : <span style={{ color: '#dc2626', fontWeight: '600' }}>Missing</span>}</div>
                <div><strong>Module:</strong> {companyToActivate.assigned_modules && companyToActivate.assigned_modules.length > 0 ? companyToActivate.assigned_modules.map(m => m.name).join(', ') : <span style={{ color: '#dc2626', fontWeight: '600' }}>Missing</span>}</div>
              </div>
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0 0 20px 0', textAlign: 'center' }}>
                If anything above is missing or incorrect, cancel and use Edit first — activation will be blocked until it's complete.
              </p>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                <button onClick={() => setCompanyToActivate(null)}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#334155', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
                <button onClick={handleConfirmActivate}
                  style={{ padding: '8px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#2563eb', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' }}>Activate</button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default CompanyManagement;
